import { db, auth } from '../firebase';
import { collection, getDocs, doc, getDoc, getDocFromServer, onSnapshot } from 'firebase/firestore';
import { env } from '../config/environment';
import { DEFAULT_VISIBLE_PAGES, applyPageGrants, normalisePageGrants } from '../constants/pageVisibility';
import { normalizeAdvisoryPortalPageIds, LEGACY_ADVISORY_PORTAL_TABS } from '../constants/advisoryPortalConfig';
import { normalizeAdminPageIds, ALL_ADMIN_TAB_NAMES } from '../constants/adminPages';
import { advisoryRoleLabel, supportAccessRole } from '../constants/advisoryRoles';
import { getCurrentAdviserEmail } from '../utils/currentAdviser';
import { isHeldFromParaplanners } from '../utils/allocationVisibility';
import {
  isUnreleasedCsvMerge as isUnreleasedCsvMergeClient,
  isUnreleasedManualAr as isUnreleasedManualArClient,
} from '../constants/csvMerge';

class UserTeamService {
  // Cache for performance
  _cache = {
    teams: {},
    users: {},
    authorizations: {},
    pageVisibility: null,
    lastFetch: null,
    loading: false
  };

  _pageVisibilityRevision = 0;
  _visibilityListeners = new Set();
  _navigationUnsubscribe = null;
  _rosterSyncLastRun = null;
  _rosterSyncInFlight = null;
  _inFlightLoad = null;

  _cacheTimeout = 5 * 60 * 1000; // 5 minutes

  _defaultPageVisibility() {
    return {
      marketing: { visiblePages: DEFAULT_VISIBLE_PAGES.marketing },
      advisory: { visiblePages: DEFAULT_VISIBLE_PAGES.advisory },
      superAdmin: { visiblePages: DEFAULT_VISIBLE_PAGES.superAdmin },
      ufSupport: { visiblePages: DEFAULT_VISIBLE_PAGES.ufSupport },
      arSupport: { visiblePages: DEFAULT_VISIBLE_PAGES.arSupport },
      arAdviser: { visiblePages: DEFAULT_VISIBLE_PAGES.arAdviser },
      management: { visiblePages: DEFAULT_VISIBLE_PAGES.management },
      compliance: { visiblePages: DEFAULT_VISIBLE_PAGES.compliance },
      paraplanner: { visiblePages: DEFAULT_VISIBLE_PAGES.paraplanner },
      paraplannerManagement: { visiblePages: DEFAULT_VISIBLE_PAGES.paraplannerManagement },
      pageGrants: normalisePageGrants(null),
    };
  }

  _buildPageVisibilityFromFirestore(firestoreData) {
    const normalizePages = (pages) => normalizeAdvisoryPortalPageIds(pages);
    const toVisibility = (pages) => ({ visiblePages: normalizePages(pages) });

    const superAdminPages = normalizePages(firestoreData.superAdmin?.visiblePages || DEFAULT_VISIBLE_PAGES.superAdmin);
    if (!superAdminPages.includes('advisoryPortal')) {
      const hadLegacyPortal = (firestoreData.superAdmin?.visiblePages || []).some(
        (id) => LEGACY_ADVISORY_PORTAL_TABS.includes(id)
      );
      if (hadLegacyPortal || !firestoreData.superAdmin?.visiblePages) {
        superAdminPages.push('advisoryPortal');
      }
    }

    return {
      marketing: toVisibility(firestoreData.marketing?.visiblePages || DEFAULT_VISIBLE_PAGES.marketing),
      advisory: toVisibility(firestoreData.advisory?.visiblePages || DEFAULT_VISIBLE_PAGES.advisory),
      superAdmin: { visiblePages: superAdminPages },
      ufSupport: toVisibility(firestoreData.ufSupport?.visiblePages || DEFAULT_VISIBLE_PAGES.ufSupport),
      arSupport: toVisibility(firestoreData.arSupport?.visiblePages || DEFAULT_VISIBLE_PAGES.arSupport),
      arAdviser: toVisibility(firestoreData.arAdviser?.visiblePages || DEFAULT_VISIBLE_PAGES.arAdviser),
      management: toVisibility(firestoreData.management?.visiblePages || DEFAULT_VISIBLE_PAGES.management),
      compliance: toVisibility(firestoreData.compliance?.visiblePages || DEFAULT_VISIBLE_PAGES.compliance),
      paraplanner: toVisibility(firestoreData.paraplanner?.visiblePages || DEFAULT_VISIBLE_PAGES.paraplanner),
      paraplannerManagement: toVisibility(firestoreData.paraplannerManagement?.visiblePages || DEFAULT_VISIBLE_PAGES.paraplannerManagement),
      // Pages granted to individual people (Page Visibility → Per person).
      pageGrants: normalisePageGrants(firestoreData.pageGrants),
    };
  }

  _setPageVisibility(firestoreData, { notify = true } = {}) {
    this._cache.pageVisibility = firestoreData
      ? this._buildPageVisibilityFromFirestore(firestoreData)
      : this._defaultPageVisibility();
    if (notify) {
      this._pageVisibilityRevision += 1;
      this._visibilityListeners.forEach((listener) => listener(this._pageVisibilityRevision));
    }
  }

  _ensurePageVisibilityLoaded() {
    if (!this._cache.pageVisibility) {
      this._setPageVisibility(null, { notify: false });
    }
  }

  getPageVisibilityRevision() {
    return this._pageVisibilityRevision;
  }

  /**
   * Live updates from app_settings/navigation. Callback receives revision number.
   * @returns {() => void} unsubscribe
   */
  subscribeToPageVisibility(callback) {
    if (typeof callback === 'function') {
      this._visibilityListeners.add(callback);
      callback(this._pageVisibilityRevision);
    }

    if (!this._navigationUnsubscribe) {
      const navRef = doc(db, 'app_settings', 'navigation');
      this._navigationUnsubscribe = onSnapshot(
        navRef,
        (snap) => {
          this._setPageVisibility(snap.exists() ? snap.data() : null);
        },
        (error) => {
          console.error('Page visibility subscription error:', error);
          // A listener that errors (dropped connection, or started before sign-in
          // completed) is dead: clear it and try again while anyone is listening.
          this._navigationUnsubscribe = null;
          if (this._visibilityListeners.size > 0) {
            setTimeout(() => {
              if (!this._navigationUnsubscribe && this._visibilityListeners.size > 0) this.subscribeToPageVisibility(null);
            }, 5000);
          }
        }
      );
    }

    return () => {
      if (typeof callback === 'function') {
        this._visibilityListeners.delete(callback);
      }
    };
  }

  hasUserConfig(email) {
    return Boolean(this.getUserConfig(email));
  }

  async _loadCache() {
    const now = Date.now();
    
    // Return if cache is still valid
    if (this._cache.lastFetch && (now - this._cache.lastFetch) < this._cacheTimeout) {
      return true;
    }

    // Prevent multiple simultaneous loads
    if (this._cache.loading) {
      // Wait for ongoing load
      await this._inFlightLoad;
      // The load we waited on succeeded: done. If it failed (dropped connection,
      // or it ran before sign-in finished) fall through and load again — a
      // forced refresh used to return here with an empty cache and the app sat
      // on "Loading your workspace…" forever.
      if (this._cache.lastFetch) return true;
    }

    this._cache.loading = true;
    this._cache.lastError = null;
    this._inFlightLoad = this._fetchCache(now);
    return this._inFlightLoad;
  }

  async _fetchCache(now) {
    try {
      // Load all data from Firestore — in parallel: on a phone each sequential
      // round trip added visibly to the startup loading screen.
      const navRef = doc(db, 'app_settings', 'navigation');
      const navPromise = getDocFromServer(navRef).catch(() => getDoc(navRef));
      navPromise.catch(() => {}); // awaited below; avoid an unhandled rejection meanwhile
      const [teamsSnap, usersSnap, authsSnap] = await Promise.all([
        getDocs(collection(db, 'teams')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'authorizations')),
      ]);

      this._cache.teams = {};
      teamsSnap.forEach(doc => {
        this._cache.teams[doc.id] = doc.data();
      });

      this._cache.users = {};
      usersSnap.forEach(doc => {
        const key = (doc.id || '').trim().toLowerCase();
        if (key) this._cache.users[key] = doc.data();
      });

      this._cache.authorizations = {};
      authsSnap.forEach(doc => {
        this._cache.authorizations[doc.id] = doc.data();
      });

      // Load page visibility settings (server read so post-save refetch gets latest)
      try {
        const visibilityDoc = await navPromise;
        this._setPageVisibility(visibilityDoc.exists() ? visibilityDoc.data() : null, { notify: false });
      } catch (error) {
        console.error('Error loading page visibility settings:', error);
        this._setPageVisibility(null, { notify: false });
      }

      this._cache.lastFetch = now;
      void this._syncTeamRostersFromServer();
      return true;
    } catch (error) {
      console.error('Error loading cache from Firestore:', error);
      this._cache.lastError = error;
      return false;
    } finally {
      this._cache.loading = false;
    }
  }

  /** True when teams/users cache has been loaded (may still be within TTL). */
  isCacheReady() {
    return Boolean(this._cache.lastFetch);
  }

  // Initialize cache (call this early in app lifecycle)
  /** @returns {Promise<boolean>} true when the teams/users cache loaded */
  async initialize() {
    return this._loadCache();
  }

  // Invalidate cache so next initialize() refetches (e.g. after user disconnects calendar)
  invalidateCache() {
    this._cache.lastFetch = null;
    this._rosterSyncLastRun = null;
  }

  getUserConfig(email) {
    const key = (email || '').trim().toLowerCase();
    // Return cached data synchronously if available (lookup by lowercase so connect/disconnect state matches backend)
    if (key && this._cache.users[key]) {
      return this._cache.users[key];
    }
    
    // Trigger async load in background if cache is empty (the collections are
    // unreadable while signed out, so a load from the login screen only fails)
    if (!this._cache.lastFetch && !this._cache.loading && auth.currentUser) {
      this._loadCache();
    }
    
    return null;
  }

  getUserRole(email) {
    const config = this.getUserConfig(email);
    return config?.role || null;
  }

  getUserName(email) {
    const config = this.getUserConfig(email);
    return config?.name || email;
  }

  getUserSmsConfig(email) {
    const config = this.getUserConfig(email);
    return {
      smsNumber: config?.smsNumber || null,
      smsEnabled: Boolean(config?.smsEnabled && config?.smsNumber),
    };
  }

  /** Super admin may be assigned to any Advice Journey task regardless of team/role. */
  isAssignableToAllTasks(email) {
    const config = this.getUserConfig(email);
    return config?.role === 'super_admin' && Boolean(config?.assignableToAllTasks);
  }

  /** Super admins with assignableToAllTasks — merged into all task assignee dropdowns. */
  getGlobalTaskAssigneeOptions() {
    const users = [];
    Object.entries(this._cache.users).forEach(([userEmail, userData]) => {
      if (userData.role === 'super_admin' && userData.assignableToAllTasks) {
        users.push(this._buildUserOption(userEmail, userData));
      }
    });
    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * All users a super admin can start a support chat with: everyone except
   * super admins (their widget is in inbox mode, so a personal thread would
   * never be seen) and the caller. Emails lowercased to match thread ids.
   */
  getSupportChatRecipients(excludeEmail = null) {
    const excluded = (excludeEmail || '').trim().toLowerCase();
    const byEmail = new Map();
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      const key = (email || '').trim().toLowerCase();
      if (!key || key === excluded) return;
      if (userData.role === 'super_admin') return;
      if (!byEmail.has(key)) {
        byEmail.set(key, {
          email: key,
          name: userData.name || key,
          role: userData.role || null,
        });
      }
    });
    return Array.from(byEmail.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Full name for display (first + last when available, else name/displayName/email).
   */
  getFullName(email) {
    const config = this.getUserConfig(email);
    if (!config) return email || '';
    const firstLast = [config.firstName, config.lastName].filter(Boolean).join(' ').trim();
    if (firstLast) return firstLast;
    return config.name || config.displayName || email || '';
  }

  /**
   * Team name for the user based on their role (marketing, advisory, or paraplanner team).
   */
  getUserTeamName(email) {
    const config = this.getUserConfig(email);
    if (!config) return null;
    if (config.marketingTeam) return this.getTeamName(config.marketingTeam);
    if (config.advisoryTeam) return this.getTeamName(config.advisoryTeam);
    if (config.paraplannerTeam) return this.getTeamName(config.paraplannerTeam);
    return null;
  }

  getTeamName(teamId) {
    // Return cached data synchronously if available
    if (this._cache.teams[teamId]) {
      return this._cache.teams[teamId].name;
    }
    
    // Trigger async load in background if cache is empty (the collections are
    // unreadable while signed out, so a load from the login screen only fails)
    if (!this._cache.lastFetch && !this._cache.loading && auth.currentUser) {
      this._loadCache();
    }
    
    return teamId;
  }

  /**
   * Compact label for portal client-list columns.
   * Prefers optional listNickname; falls back to full team name.
   */
  getTeamListLabel(teamId) {
    if (!teamId) return '';
    const team = this.getTeamData(teamId);
    const nick = typeof team?.listNickname === 'string' ? team.listNickname.trim() : '';
    if (nick) return nick;
    return this.getTeamName(teamId);
  }

  getTeamData(teamId) {
    if (!teamId) return null;
    if (this._cache.teams[teamId]) {
      return this._cache.teams[teamId];
    }
    if (!this._cache.lastFetch && !this._cache.loading) {
      this._loadCache();
    }
    return null;
  }

  getDefaultDocusignCcRecipients(advisoryTeamId) {
    const team = this.getTeamData(advisoryTeamId);
    return Array.isArray(team?.defaultDocusignCcRecipients)
      ? team.defaultDocusignCcRecipients.filter((entry) => entry?.email)
      : [];
  }

  getMarketingTeams() {
    const marketing = {};
    Object.entries(this._cache.teams).forEach(([id, team]) => {
      if (team.type === 'marketing') {
        marketing[id] = team.name;
      }
    });
    return marketing;
  }

  getAdvisoryTeams() {
    const advisory = {};
    Object.entries(this._cache.teams).forEach(([id, team]) => {
      if (team.type === 'advisory') {
        advisory[id] = team.name;
      }
    });
    return advisory;
  }

  getParaplannerTeams() {
    const paraplanner = {};
    Object.entries(this._cache.teams).forEach(([id, team]) => {
      if (team.type === 'paraplanner') {
        paraplanner[id] = team.name;
      }
    });
    return paraplanner;
  }

  getAuthorizedAdvisoryTeams(marketingTeamId) {
    const auth = this._cache.authorizations[marketingTeamId];
    if (!auth || !auth.advisoryTeams || auth.advisoryTeams.length === 0) {
      // No restrictions - return all advisory teams
      return Object.keys(this.getAdvisoryTeams());
    }
    return auth.advisoryTeams;
  }

  /**
   * True when the marketing team has an explicit non-empty authorization list.
   * Empty / missing authorizations mean "any advice team" (admin UI copy).
   */
  hasRestrictedAdvisoryAuthorizations(marketingTeamId) {
    if (!marketingTeamId) return false;
    const auth = this._cache.authorizations[marketingTeamId];
    return Array.isArray(auth?.advisoryTeams) && auth.advisoryTeams.length > 0;
  }

  /**
   * True when the paraplanner team has an explicit non-empty authorization list.
   * Empty / missing authorizations mean "all advice firms" (admin UI copy).
   */
  hasRestrictedParaplannerAuthorizations(paraplannerTeamId) {
    if (!paraplannerTeamId) return false;
    const auth = this._cache.authorizations[paraplannerTeamId];
    return Array.isArray(auth?.advisoryTeams) && auth.advisoryTeams.length > 0;
  }

  getParaplannerAuthorizedAdvisoryTeams(paraplannerTeamId) {
    const auth = this._cache.authorizations[paraplannerTeamId];
    return Array.isArray(auth?.advisoryTeams) ? auth.advisoryTeams : [];
  }

  /**
   * Firm scoping for paraplanners: a restricted team sees only clients assigned
   * to one of its authorized advice firms (clients with no assigned advisory
   * team are hidden — fail closed). Unrestricted team, or no team, sees all.
   */
  _paraplannerClientInScope(config, client) {
    if (!this.hasRestrictedParaplannerAuthorizations(config.paraplannerTeam)) return true;
    const assignedAdvisory = client.assignment?.assignedAdvisory;
    return Boolean(assignedAdvisory) &&
      this.getParaplannerAuthorizedAdvisoryTeams(config.paraplannerTeam).includes(assignedAdvisory);
  }

  /**
   * Marketing team id used for authorization: client assignment first, else the
   * logged-in marketing user's own team.
   */
  resolveMarketingTeamForAuthorization(userEmail, selectedMarketingTeam = '') {
    const fromClient = (selectedMarketingTeam || '').trim();
    if (fromClient) return fromClient;
    const userConfig = this.getUserConfig(userEmail);
    if (userConfig?.role === 'marketing' && userConfig.marketingTeam) {
      return String(userConfig.marketingTeam).trim();
    }
    return '';
  }

  isAdvisoryAuthorizedForMarketingTeam(marketingTeamId, advisoryTeamId) {
    const advisoryId = (advisoryTeamId || '').trim();
    if (!advisoryId) return true;
    const marketingId = (marketingTeamId || '').trim();
    if (!marketingId || !this.hasRestrictedAdvisoryAuthorizations(marketingId)) {
      return true;
    }
    return this.getAuthorizedAdvisoryTeams(marketingId).includes(advisoryId);
  }

  _normalizeAccessibleTabs(pages) {
    const mapped = (Array.isArray(pages) ? pages : []).map((id) => (
      id === 'importWorkbench' ? 'csvMerge' : id
    ));
    // The cross-client Messages inbox (Sept 2026) goes to everyone who can open a
    // client's messages, so stored Page Visibility lists need no update.
    if (mapped.includes('messages') && !mapped.includes('messagesInbox')) mapped.push('messagesInbox');
    const advisory = normalizeAdvisoryPortalPageIds(mapped);
    return normalizeAdminPageIds(advisory);
  }

  /**
   * The pages this person can open: their role's list plus anything granted to
   * them by name (Page Visibility → Per person). "View as role" previews a role,
   * so it ignores personal grants (getAccessibleTabsForRole).
   */
  getAccessibleTabs(email) {
    const roleTabs = this._roleAccessibleTabs(email);
    if (!this.getUserConfig(email)) return roleTabs;
    return applyPageGrants(roleTabs, this._cache.pageVisibility, email);
  }

  _roleAccessibleTabs(email) {
    const config = this.getUserConfig(email);
    if (!config) return [];

    this._ensurePageVisibilityLoaded();

    const visibility = this._cache.pageVisibility;

    if (config.role === 'super_admin') {
      const superAdminPages = visibility.superAdmin?.visiblePages;
      if (!superAdminPages || superAdminPages.length === 0) {
        return this._normalizeAccessibleTabs([...DEFAULT_VISIBLE_PAGES.superAdmin]);
      }
      const out = new Set(superAdminPages);
      return this._normalizeAccessibleTabs(Array.from(out));
    }

    if (config.role === 'marketing') {
      return this._normalizeAccessibleTabs(visibility.marketing?.visiblePages || DEFAULT_VISIBLE_PAGES.marketing);
    }

    if (config.role === 'advisory') {
      // A provisional adviser sees exactly AR Support's pages.
      const advisoryRole = supportAccessRole(config.advisoryRole);
      if (advisoryRole === 'ufSupport') {
        return this._normalizeAccessibleTabs(visibility.ufSupport?.visiblePages || DEFAULT_VISIBLE_PAGES.ufSupport);
      }
      if (advisoryRole === 'arSupport') {
        return this._normalizeAccessibleTabs(visibility.arSupport?.visiblePages || DEFAULT_VISIBLE_PAGES.arSupport);
      }
      if (advisoryRole === 'support') {
        console.warn(`User has legacy advisoryRole='support'. Reassign to 'ufSupport' or 'arSupport' via Advisory Team editor.`);
        return this._normalizeAccessibleTabs(visibility.advisory?.visiblePages || DEFAULT_VISIBLE_PAGES.advisory);
      }
      if (advisoryRole === 'arAdviser') {
        return this._normalizeAccessibleTabs(visibility.arAdviser?.visiblePages || DEFAULT_VISIBLE_PAGES.arAdviser);
      }
      if (advisoryRole === 'management') {
        return this._normalizeAccessibleTabs(visibility.management?.visiblePages || DEFAULT_VISIBLE_PAGES.management);
      }
      if (advisoryRole === 'compliance') {
        return this._normalizeAccessibleTabs(visibility.compliance?.visiblePages || DEFAULT_VISIBLE_PAGES.compliance);
      }
      return this._normalizeAccessibleTabs(visibility.advisory?.visiblePages || DEFAULT_VISIBLE_PAGES.advisory);
    }

    if (config.role === 'paraplanner') {
      if (config.paraplannerRole === 'management') {
        return this._normalizeAccessibleTabs(visibility.paraplannerManagement?.visiblePages || DEFAULT_VISIBLE_PAGES.paraplannerManagement);
      }
      return this._normalizeAccessibleTabs(visibility.paraplanner?.visiblePages || DEFAULT_VISIBLE_PAGES.paraplanner);
    }

    return this._normalizeAccessibleTabs(DEFAULT_VISIBLE_PAGES.marketing);
  }

  canAccessTab(email, tabName) {
    if (tabName === 'arWizard') tabName = 'advice';
    if (tabName === 'clients') tabName = 'advisoryPortal';
    if (LEGACY_ADVISORY_PORTAL_TABS.includes(tabName)) tabName = 'advisoryPortal';
    if (tabName === 'admin') {
      const tabs = this.getAccessibleTabs(email);
      if (tabs.includes('admin')) return true;
      return ALL_ADMIN_TAB_NAMES.some((t) => tabs.includes(t));
    }
    const accessibleTabs = this.getAccessibleTabs(email);
    const normalizedTabs = accessibleTabs.map((t) =>
      LEGACY_ADVISORY_PORTAL_TABS.includes(t) ? 'advisoryPortal' : t
    );
    if (normalizedTabs.includes(tabName)) return true;
    if (tabName === 'adviceJourney') return normalizedTabs.includes('implementationChecklist');
    if (tabName === 'implementationChecklist') return normalizedTabs.includes('adviceJourney');
    return false;
  }

  /**
   * Get accessible tab names for a "view as role" key (admin only).
   * Used when super_admin has ?viewAs=ga|paraplanner|adviser|ufSupport|arSupport|arAdviser in URL.
   * @param {string} viewAsKey - 'ga' | 'paraplanner' | 'adviser' | 'ufSupport' | 'arSupport' | 'arAdviser' | 'management'
   * @returns {string[]} tab names
   */
  getAccessibleTabsForRole(viewAsKey) {
    this._ensurePageVisibilityLoaded();
    const map = {
      ga: 'marketing',
      paraplanner: 'paraplanner',
      paraplannerManagement: 'paraplannerManagement',
      adviser: 'advisory',
      management: 'management',
      ufSupport: 'ufSupport',
      arSupport: 'arSupport',
      arAdviser: 'arAdviser',
      compliance: 'compliance'
    };
    const visibilityKey = map[viewAsKey];
    if (!visibilityKey) return this._normalizeAccessibleTabs(DEFAULT_VISIBLE_PAGES.marketing);
    const visibility = this._cache.pageVisibility;
    if (!visibility || !visibility[visibilityKey]) {
      return this._normalizeAccessibleTabs(DEFAULT_VISIBLE_PAGES[visibilityKey] || DEFAULT_VISIBLE_PAGES.marketing);
    }
    return this._normalizeAccessibleTabs(visibility[visibilityKey].visiblePages || DEFAULT_VISIBLE_PAGES[visibilityKey]);
  }

  canUserAccessClient(email, client) {
    const config = this.getUserConfig(email);
    if (!config) return false;
    
    if (config.role === "super_admin") return true;
    
    // Always allow access if user created the client (for newly created clients)
    if (client.createdBy === email) {
      return true;
    }
    
    if (config.role === "marketing") {
      if (client.isTestClient === true) return false;
      return client.assignment?.assignedMarketingTeam === config.marketingTeam;
    }
    
    if (config.role === "advisory") {
      // Test clients stay out of advisers' lists, but their Assigned Adviser can
      // open one (e.g. from a task link) to try the adviser side of a workflow.
      if (client.isTestClient === true
        && getCurrentAdviserEmail(client) !== (email || '').trim().toLowerCase()) return false;
      if (this.isUnreleasedCsvMerge(client)) return false;
      return client.assignment?.assignedAdvisory === config.advisoryTeam;
    }

    if (config.role === "paraplanner") {
      // Paraplanners see clients at every step — visibility limited only by portal
      // filter settings and their team's authorized advice firms.
      if (client.isLost) return false;
      if (this.isUnreleasedCsvMerge(client)) return false;
      if (isHeldFromParaplanners(client)) return false;
      return this._paraplannerClientInScope(config, client);
    }

    return false;
  }

  /**
   * Unreleased CSV-merge clients stay in the CSV Merge portal until released.
   */
  isUnreleasedCsvMerge(client) {
    return isUnreleasedCsvMergeClient(client);
  }

  /** Unreleased manually-entered AR clients — shown only behind the portal "Show unreleased AR" toggle. */
  isUnreleasedManualAr(client) {
    return isUnreleasedManualArClient(client);
  }

  /** Any unreleased bucketed client (CSV merge import or manual AR entry). */
  isUnreleasedClient(client) {
    return isUnreleasedCsvMergeClient(client) || isUnreleasedManualArClient(client);
  }

  /**
   * Produce the list of clients a user should see.
   *
   * @param {string} email
   * @param {Array} allClients
   * @param {{ includeUnreleasedCsvMerge?: boolean, includeUnreleasedManualAr?: boolean }} [opts]
   *   Set `includeUnreleasedCsvMerge: true` for the CSV Merge portal subscriber.
   *   Set `includeUnreleasedManualAr: true` for portals with a "Show unreleased AR" toggle.
   */
  getVisibleClients(email, allClients, opts = {}) {
    const config = this.getUserConfig(email);
    if (!config) return [];
    const hideAsUnreleased = (client) => (
      (!opts.includeUnreleasedCsvMerge && this.isUnreleasedCsvMerge(client))
      || (!opts.includeUnreleasedManualAr && this.isUnreleasedManualAr(client))
    );

    if (config.role === "super_admin") {
      return allClients.filter((c) => !hideAsUnreleased(c));
    }

    return allClients.filter(client => {
      if (hideAsUnreleased(client)) return false;

      if (config.role === "marketing") {
        if (client.isTestClient === true) return false;
        return client.assignment?.assignedMarketingTeam === config.marketingTeam;
      }

      if (config.role === "advisory") {
        if (client.isTestClient === true) return false;
        return client.assignment?.assignedAdvisory === config.advisoryTeam ||
               client.createdBy === email;
      }

      if (config.role === "paraplanner") {
        // Paraplanners see clients at every step — visibility limited only by portal
        // filter settings and their team's authorized advice firms — except a client their firm
        // is still allocating (src/utils/allocationVisibility.js).
        return !client.isLost && !isHeldFromParaplanners(client) && this._paraplannerClientInScope(config, client);
      }

      return false;
    });
  }

  // Get advisory team options filtered by authorization
  getAdvisoryTeamOptions(userEmail, selectedMarketingTeam) {
    const advisoryTeams = this.getAdvisoryTeams();
    const userConfig = this.getUserConfig(userEmail);
    const toOptions = (teamsMap) => Object.entries(teamsMap).map(([id, name]) => ({
      value: id,
      label: name,
    }));

    // Super admins see all teams
    if (userConfig?.role === 'super_admin') {
      return toOptions(advisoryTeams);
    }

    // Marketing users: always filter by authorized advice teams for the
    // client marketing team, falling back to the user's own marketing team.
    const authMarketingTeam = this.resolveMarketingTeamForAuthorization(
      userEmail,
      selectedMarketingTeam,
    );
    if (userConfig?.role === 'marketing' || authMarketingTeam) {
      if (!authMarketingTeam) {
        return toOptions(advisoryTeams);
      }
      const authorizedTeams = this.getAuthorizedAdvisoryTeams(authMarketingTeam);
      const filtered = {};
      authorizedTeams.forEach((teamId) => {
        if (advisoryTeams[teamId]) {
          filtered[teamId] = advisoryTeams[teamId];
        }
      });
      return toOptions(filtered);
    }

    return toOptions(advisoryTeams);
  }

  // Helper methods for backward compatibility
  canUserEditMarketing(email) {
    const role = this.getUserRole(email);
    return role === "advisory" || role === "super_admin";
  }

  canUserEditAdvisory(email) {
    const role = this.getUserRole(email);
    return role === "marketing" || role === "super_admin";
  }

  /**
   * Marketing team options for assignment dropdowns. Marketing users only ever
   * see their own team — listing the other marketing firms is a conflict of
   * interest (docs/CROSS_FIRM_DATA_EXPOSURE_AUDIT.md, finding 5). Unknown
   * users get nothing (fail closed); other roles see the full list.
   */
  getMarketingTeamOptions(userEmail) {
    const config = this.getUserConfig(userEmail);
    if (!config) return [];

    const teams = this.getMarketingTeams();
    if (config.role === 'marketing') {
      const ownTeam = String(config.marketingTeam || '').trim();
      if (!ownTeam) return [];
      return [{ value: ownTeam, label: teams[ownTeam] || this.getTeamName(ownTeam) }];
    }

    return Object.entries(teams).map(([id, name]) => ({
      value: id,
      label: name
    }));
  }

  _getTeamMemberEmailSet(teamId) {
    const team = this._cache.teams?.[teamId];
    if (!team || !Array.isArray(team.memberEmails)) {
      return null;
    }
    if (team.memberEmails.length === 0) {
      return new Set();
    }
    return new Set(team.memberEmails.map((email) => (email || '').trim().toLowerCase()).filter(Boolean));
  }

  _getAllTeamRosterEmails(teamType) {
    const emails = new Set();
    let hasAnyRoster = false;

    Object.values(this._cache.teams || {}).forEach((team) => {
      if (team?.type !== teamType) return;
      if (!Array.isArray(team.memberEmails) || team.memberEmails.length === 0) return;
      hasAnyRoster = true;
      team.memberEmails.forEach((email) => {
        const key = (email || '').trim().toLowerCase();
        if (key) emails.add(key);
      });
    });

    return hasAnyRoster ? emails : null;
  }

  _isOnTeamRoster(teamId, email) {
    const roster = this._getTeamMemberEmailSet(teamId);
    // No roster on team doc yet, or empty roster — match users by role/team only
    if (!roster || roster.size === 0) return true;
    const key = (email || '').trim().toLowerCase();
    return Boolean(key && roster.has(key));
  }

  _dedupeUserOptions(options) {
    const seen = new Set();
    return options.filter((opt) => {
      const key = (opt.value || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  async _syncTeamRostersFromServer({ force = false } = {}) {
    if (!auth.currentUser) return;

    const now = Date.now();
    if (!force && this._rosterSyncLastRun && (now - this._rosterSyncLastRun) < this._cacheTimeout) {
      return;
    }
    if (this._rosterSyncInFlight) {
      return this._rosterSyncInFlight;
    }

    this._rosterSyncInFlight = (async () => {
      try {
        const idToken = await auth.currentUser.getIdToken();
        const response = await fetch(env.apiEndpoints.teamRosters, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!response.ok) return;

        const data = await response.json();
        Object.entries(data.rosters || {}).forEach(([teamId, memberEmails]) => {
          if (!this._cache.teams[teamId]) {
            this._cache.teams[teamId] = {};
          }
          this._cache.teams[teamId].memberEmails = Array.isArray(memberEmails) ? memberEmails : [];
        });
        this._rosterSyncLastRun = Date.now();
      } catch (error) {
        console.warn('Team roster sync failed:', error);
      } finally {
        this._rosterSyncInFlight = null;
      }
    })();

    return this._rosterSyncInFlight;
  }

  _buildUserOption(email, userData) {
    return {
      value: email,
      label: userData?.name || email,
    };
  }

  /** Advisers, paraplanners and super admins: who a page can be granted to by name (Page Visibility → Per person). */
  getPageGrantCandidates() {
    const options = [...this.getAllAdvisoryUsers(), ...this.getAllParaplannerUsers()];
    Object.entries(this._cache.users || {}).forEach(([email, userData]) => {
      if (userData?.role === 'super_admin') options.push(this._buildUserOption(email, userData));
    });
    return this._dedupeUserOptions(options).sort((a, b) => a.label.localeCompare(b.label));
  }

  // Get users by marketing team for marketer assignment
  getUsersByMarketingTeam(marketingTeamId) {
    if (!marketingTeamId) {
      return [];
    }

    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role === 'marketing' && userData.marketingTeam === marketingTeamId) {
        if (!this._isOnTeamRoster(marketingTeamId, email)) return;
        users.push(this._buildUserOption(email, userData));
      }
    });

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  _mapPassToMarketingRecipientsFromTeamDoc(marketingTeamId) {
    const mirrored = this._cache.teams?.[marketingTeamId]?.passToMarketingRecipients;
    if (!Array.isArray(mirrored) || mirrored.length === 0) {
      return null;
    }

    const users = mirrored
      .map((entry) => {
        const email = (entry?.email || '').trim();
        if (!email) return null;
        const key = email.toLowerCase();
        const userData = this._cache.users[key];
        return {
          value: email,
          label: entry.displayName || userData?.name || email,
          preSelected: entry.preselected === true,
        };
      })
      .filter(Boolean);

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  /** Fetch mirrored pass-to-marketing recipients for one team (single Firestore read). */
  async refreshPassToMarketingRecipientsForTeam(marketingTeamId) {
    if (!marketingTeamId) return;

    try {
      const teamRef = doc(db, 'teams', marketingTeamId);
      let teamSnap;
      try {
        teamSnap = await getDocFromServer(teamRef);
      } catch {
        teamSnap = await getDoc(teamRef);
      }
      if (!teamSnap.exists()) return;

      if (!this._cache.teams[marketingTeamId]) {
        this._cache.teams[marketingTeamId] = {};
      }
      Object.assign(this._cache.teams[marketingTeamId], teamSnap.data());
    } catch (error) {
      console.warn('Failed to refresh pass-to-marketing recipients:', error);
    }
  }

  /** Pass to Marketing modal — marketing users flagged in Admin → Marketing Teams. */
  getPassToMarketingRecipientsByMarketingTeam(marketingTeamId) {
    if (!marketingTeamId) {
      return [];
    }

    const fromTeamDoc = this._mapPassToMarketingRecipientsFromTeamDoc(marketingTeamId);
    if (fromTeamDoc?.length) {
      return fromTeamDoc;
    }

    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role !== 'marketing' || userData.marketingTeam !== marketingTeamId) return;
      if (!this._isOnTeamRoster(marketingTeamId, email)) return;
      if (userData.passToMarketingRecipient !== true) return;
      users.push({
        ...this._buildUserOption(email, userData),
        preSelected: userData.passToMarketingPreselected === true,
      });
    });

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  // Get all marketing users across all teams (for super admin filter)
  getAllMarketingUsers() {
    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role === 'marketing') {
        users.push({
          value: email,
          label: userData.name || email
        });
      }
    });
    // Sort by label (name) for better UX
    return users.sort((a, b) => a.label.localeCompare(b.label));
  }

  getUsersByParaplannerTeam(paraplannerTeamId) {
    if (!paraplannerTeamId) {
      return [];
    }

    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role === 'paraplanner' && userData.paraplannerTeam === paraplannerTeamId) {
        if (!this._isOnTeamRoster(paraplannerTeamId, email)) return;
        users.push(this._buildUserOption(email, userData));
      }
    });

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  getAllParaplannerUsers() {
    const rosterEmails = this._getAllTeamRosterEmails('paraplanner');
    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role !== 'paraplanner') return;
      if (rosterEmails && !rosterEmails.has(email)) return;
      if (
        userData.paraplannerTeam &&
        !this._isOnTeamRoster(userData.paraplannerTeam, email)
      ) {
        return;
      }
      users.push(this._buildUserOption(email, userData));
    });
    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Users whose saved client-list views a super admin can manage for a portal
   * ('advisory' | 'paraplanner'): the same rosters as Admin → Default Filters.
   * → [{ email, name, roleLabel }] sorted by name.
   */
  getClientListViewOwners(portalKey) {
    const options = portalKey === 'paraplanner' ? this.getAllParaplannerUsers() : this.getAllAdvisoryUsers();
    return options.map(({ value, label }) => {
      const config = this._cache.users[value] || {};
      const roleLabel = portalKey === 'paraplanner'
        ? (config.paraplannerRole === 'management' ? 'Paraplanner management' : 'Paraplanner')
        : advisoryRoleLabel(config.advisoryRole);
      return { email: String(value).toLowerCase(), name: label, roleLabel };
    });
  }

  /** All advisory-role users (every team / advisoryRole) for task assignee dropdowns. */
  getAllAdvisoryUsers() {
    const rosterEmails = this._getAllTeamRosterEmails('advisory');
    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role !== 'advisory') return;
      if (rosterEmails && !rosterEmails.has(email)) return;
      if (userData.advisoryTeam && !this._isOnTeamRoster(userData.advisoryTeam, email)) {
        return;
      }
      users.push(this._buildUserOption(email, userData));
    });
    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  // Get users by advisory team for email recipients
  getUsersByAdvisoryTeam(advisoryTeamId, advisoryRoleFilter = null) {
    if (!advisoryTeamId) {
      return [];
    }

    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role === 'advisory' && userData.advisoryTeam === advisoryTeamId) {
        if (!this._isOnTeamRoster(advisoryTeamId, email)) return;
        // Apply advisory role filter if specified (one role or a list)
        if (advisoryRoleFilter) {
          const allowed = Array.isArray(advisoryRoleFilter) ? advisoryRoleFilter : [advisoryRoleFilter];
          if (!allowed.includes(userData.advisoryRole)) return;
        }
        users.push(this._buildUserOption(email, userData));
      }
    });

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  // Get advisers by advisory team (for adviser selector in assignment section)
  getAdvisersByAdvisoryTeam(advisoryTeamId) {
    return this.getUsersByAdvisoryTeam(advisoryTeamId, 'adviser');
  }

  // Get AR advisers by advisory team, provisional advisers included (adviser
  // pickers and the AR booking comparison calendars)
  getARAdvisersByAdvisoryTeam(advisoryTeamId) {
    return this.getUsersByAdvisoryTeam(advisoryTeamId, ['arAdviser', 'arProvisionalAdviser']);
  }

  // Get UF support staff by advisory team
  getUfSupportStaffByAdvisoryTeam(advisoryTeamId) {
    return this.getUsersByAdvisoryTeam(advisoryTeamId, 'ufSupport');
  }

  // Get AR support staff by advisory team
  getArSupportStaffByAdvisoryTeam(advisoryTeamId) {
    return this.getUsersByAdvisoryTeam(advisoryTeamId, 'arSupport');
  }

  // Get management staff by advisory team
  getManagementByAdvisoryTeam(advisoryTeamId) {
    return this.getUsersByAdvisoryTeam(advisoryTeamId, 'management');
  }

  // Get fact find recipients by advisory team (users with factFindRecipient flag)
  getFactFindRecipientsByAdvisoryTeam(advisoryTeamId) {
    return this._getFactFindRecipientsForTeam({
      teamId: advisoryTeamId,
      role: 'advisory',
      teamField: 'advisoryTeam',
    });
  }

  getFactFindRecipientsByMarketingTeam(marketingTeamId) {
    return this._getFactFindRecipientsForTeam({
      teamId: marketingTeamId,
      role: 'marketing',
      teamField: 'marketingTeam',
    });
  }

  getFactFindRecipientsByParaplannerTeam(paraplannerTeamId) {
    return this._getFactFindRecipientsForTeam({
      teamId: paraplannerTeamId,
      role: 'paraplanner',
      teamField: 'paraplannerTeam',
    });
  }

  /** All paraplanner FF email recipients across every paraplanner team (client-agnostic). */
  getAllFactFindRecipientsByParaplanner() {
    const fromUsers = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role !== 'paraplanner') return;
      if (userData.factFindRecipient !== true) return;
      const teamId = userData.paraplannerTeam;
      if (teamId && !this._isOnTeamRoster(teamId, email)) return;
      fromUsers.push({
        ...this._buildUserOption(email, userData),
        preSelected: userData.preSelected === true,
      });
    });

    const lists = [
      this._dedupeUserOptions(fromUsers).sort((a, b) => a.label.localeCompare(b.label)),
    ];

    Object.entries(this._cache.teams || {}).forEach(([teamId, team]) => {
      if (team.type !== 'paraplanner') return;
      const fromTeamDoc = this._mapFactFindRecipientsFromTeamDoc(teamId);
      if (fromTeamDoc?.length) {
        lists.push(fromTeamDoc);
      }
    });

    return this._mergeFactFindRecipientOptions(lists);
  }

  _mapJourneyEmailRecipientsFromTeamDoc(teamId) {
    const mirrored = this._cache.teams?.[teamId]?.journeyEmailRecipients;
    if (!Array.isArray(mirrored) || mirrored.length === 0) {
      return null;
    }

    const users = mirrored
      .map((entry) => {
        const email = (entry?.email || '').trim();
        if (!email) return null;
        const key = email.toLowerCase();
        const userData = this._cache.users[key];
        return {
          value: email,
          label: entry.displayName || userData?.name || email,
          preSelected: entry.preselected === true,
        };
      })
      .filter(Boolean);

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Journey email paraplanners — firm-wide across every paraplanner team.
   * Flagged via "Journey emails" in Admin → Paraplanner Teams; used for SOA
   * approval / amendment notifications and CC on advice journey emails.
   */
  getJourneyEmailParaplannerRecipients() {
    const fromUsers = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role !== 'paraplanner') return;
      if (userData.journeyEmailRecipient !== true) return;
      const teamId = userData.paraplannerTeam;
      if (teamId && !this._isOnTeamRoster(teamId, email)) return;
      fromUsers.push({
        ...this._buildUserOption(email, userData),
        preSelected: userData.journeyEmailPreselected === true,
      });
    });

    const lists = [
      this._dedupeUserOptions(fromUsers).sort((a, b) => a.label.localeCompare(b.label)),
    ];

    Object.entries(this._cache.teams || {}).forEach(([teamId, team]) => {
      if (team.type !== 'paraplanner') return;
      const fromTeamDoc = this._mapJourneyEmailRecipientsFromTeamDoc(teamId);
      if (fromTeamDoc?.length) {
        lists.push(fromTeamDoc);
      }
    });

    return this._mergeFactFindRecipientOptions(lists);
  }

  /** Fact Find Complete email — advisory, marketing, and firm-wide paraplanner recipients. */
  getFactFindRecipients({ advisoryTeamId, marketingTeamId } = {}) {
    return this._mergeFactFindRecipientOptions([
      this.getFactFindRecipientsByAdvisoryTeam(advisoryTeamId),
      this.getFactFindRecipientsByMarketingTeam(marketingTeamId),
      this.getAllFactFindRecipientsByParaplanner(),
    ]);
  }

  _getFactFindRecipientsForTeam({ teamId, role, teamField }) {
    if (!teamId) {
      return [];
    }

    const fromTeamDoc = this._mapFactFindRecipientsFromTeamDoc(teamId);
    if (fromTeamDoc?.length) {
      return fromTeamDoc;
    }

    const users = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role !== role || userData[teamField] !== teamId) return;
      if (!this._isOnTeamRoster(teamId, email)) return;
      if (userData.factFindRecipient !== true) return;
      users.push({
        ...this._buildUserOption(email, userData),
        preSelected: userData.preSelected === true,
      });
    });

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  _mapFactFindRecipientsFromTeamDoc(teamId) {
    const mirrored = this._cache.teams?.[teamId]?.factFindRecipients;
    if (!Array.isArray(mirrored) || mirrored.length === 0) {
      return null;
    }

    const users = mirrored
      .map((entry) => {
        const email = (entry?.email || '').trim();
        if (!email) return null;
        const key = email.toLowerCase();
        const userData = this._cache.users[key];
        return {
          value: email,
          label: entry.displayName || userData?.name || email,
          preSelected: entry.preselected === true,
        };
      })
      .filter(Boolean);

    return this._dedupeUserOptions(users).sort((a, b) => a.label.localeCompare(b.label));
  }

  _mergeFactFindRecipientOptions(lists) {
    const byEmail = new Map();
    lists.forEach((list) => {
      (list || []).forEach((user) => {
        const key = (user.value || '').trim().toLowerCase();
        if (!key) return;
        const existing = byEmail.get(key);
        if (!existing) {
          byEmail.set(key, { ...user });
          return;
        }
        if (user.preSelected) {
          existing.preSelected = true;
        }
      });
    });
    return [...byEmail.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  // Get all advisers with AR numbers (for TPA document)
  getAdvisersWithARNumbers(advisoryTeamId) {
    if (!advisoryTeamId) {
      return [];
    }

    const advisers = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role !== 'advisory' || userData.advisoryTeam !== advisoryTeamId) return;
      const role = userData.advisoryRole;
      if (role !== 'adviser' && role !== 'arAdviser') return;
      advisers.push({
        name: userData.name || email,
        arNumber: userData.arNumber || '',
        email,
      });
    });

    return advisers;
  }

  // Get UF support staff names (for TPA document)
  getUfSupportStaffNames(advisoryTeamId) {
    if (!advisoryTeamId) {
      return [];
    }

    const supportStaff = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role === 'advisory' &&
          userData.advisoryTeam === advisoryTeamId &&
          userData.advisoryRole === 'ufSupport') {
        supportStaff.push({
          name: userData.name || email,
          email: email
        });
      }
    });

    return supportStaff;
  }

  // Get AR support staff names (for TPA document)
  getArSupportStaffNames(advisoryTeamId) {
    if (!advisoryTeamId) {
      return [];
    }

    const supportStaff = [];
    Object.entries(this._cache.users).forEach(([email, userData]) => {
      if (userData.role === 'advisory' &&
          userData.advisoryTeam === advisoryTeamId &&
          userData.advisoryRole === 'arSupport') {
        supportStaff.push({
          name: userData.name || email,
          email: email
        });
      }
    });

    return supportStaff;
  }

  // Get default team assignments for a new client based on creator's team
  getDefaultAssignments(userEmail) {
    const config = this.getUserConfig(userEmail);
    
    if (!config) {
      return {
        assignedMarketingTeam: null,
        assignedMarketer: null,
        assignedAdvisory: null
      };
    }
    
    // If user is on a marketing team, assign to their team and themselves as marketer
    if (config.role === 'marketing' && config.marketingTeam) {
      return {
        assignedMarketingTeam: config.marketingTeam,
        assignedMarketer: userEmail,
        assignedAdvisory: null
      };
    }
    
    // If user is on an advisory team, assign to their team
    if (config.role === 'advisory' && config.advisoryTeam) {
      return {
        assignedMarketingTeam: null,
        assignedMarketer: null,
        assignedAdvisory: config.advisoryTeam
      };
    }
    
    // Super admins or unassigned users don't auto-assign
    return {
      assignedMarketingTeam: null,
      assignedMarketer: null,
      assignedAdvisory: null
    };
  }

  // Clear cache (call after creating/updating/deleting teams or users)
  clearCache() {
    this._cache = {
      teams: {},
      users: {},
      authorizations: {},
      pageVisibility: null,
      lastFetch: null,
      loading: false
    };
  }

  // Force refresh cache (bypass timeout)
  /** @returns {Promise<boolean>} true when the teams/users cache loaded */
  async forceRefreshCache() {
    this._cache.lastFetch = null;
    return this._loadCache();
  }
}

const userTeamService = new UserTeamService();
export default userTeamService;
